import { useQuery } from '@tanstack/react-query';
import { download } from '@src/utils/download-utils';
import {
  awaitJSON,
  LinearOptimizationsLambdaFetch,
  pollS3,
  S3SignedUrlFetch,
  StreamingUtilsLambdaFetch,
  assertPresignedOk
} from '@src/utils/fetch-utils';
import { StageOneData, StageTwoData } from './linear-ml-diagnostics-constants';

const CSV_MIME_TYPE = 'text/csv';

const mlDataPath = (branch: string, kpi: string, file: string): string =>
  `bpm-ml-data/${branch}/${kpi}/latest/${file}`;

// The PDF is optional, so a failed request (missing file, or the browser blocking it, e.g. S3 CORS
// on an origin bpm-ml-data doesn't allow) leaves it out instead of failing all of Stage One.
const loadImpulseResponsePdf = async (kpi: string, branch: string): Promise<Blob | undefined> => {
  try {
    const res = await S3SignedUrlFetch(
      mlDataPath(branch, kpi, 'smoothed_impulse_response_curve.pdf')
    );
    return res.ok ? await res.blob() : undefined;
  } catch {
    return undefined;
  }
};

// Stage one is processed asynchronously: the lambda kicks off a job and returns an id, and the
// processed JSON lands in S3 under that id.
const loadStageOne = async (kpi: string, branch: string): Promise<StageOneData> => {
  const { id } = await awaitJSON<{ id: string }>(
    await LinearOptimizationsLambdaFetch('/get_stage_one', { params: { kpi, branch } })
  );
  const dataBlob = await pollS3({
    bucket: 'processed-stage-one-diagnostics',
    mimeType: 'application/json',
    filename: `${id}.json`,
    autoDownload: false,
    noTimeout: true
  });
  const data: Omit<StageOneData, 'pdf'> = JSON.parse(await dataBlob.text());
  return { ...data, pdf: await loadImpulseResponsePdf(kpi, branch) };
};

export const useStageOneDiagnostics = (kpi: string | undefined, branch: string) =>
  useQuery({
    queryKey: ['linearMLStageOne', kpi, branch],
    enabled: Boolean(kpi),
    staleTime: Infinity,
    // A retry would call /get_stage_one again, which starts a second backend job.
    retry: false,
    queryFn: () => loadStageOne(kpi!, branch),
    meta: { suppressGlobalError: true }
  });

// The lambda returns `{}` when there is no stage two output for the KPI.
export const useStageTwoDiagnostics = (kpi: string | undefined, branch: string, enabled = true) =>
  useQuery({
    queryKey: ['linearMLStageTwo', kpi, branch],
    enabled: enabled && Boolean(kpi),
    staleTime: Infinity,
    queryFn: async () =>
      awaitJSON<Partial<StageTwoData>>(
        await LinearOptimizationsLambdaFetch('/stage_two_diagnostics', { params: { kpi, branch } })
      ),
    meta: { suppressGlobalError: true }
  });

export const downloadStageOneCsv = async (kpi: string, branch: string): Promise<void> => {
  const response = await S3SignedUrlFetch(
    mlDataPath(branch, kpi, 'mldiagnostics_v4_stage1_output_consolidated.csv.gz')
  );
  assertPresignedOk(response, 'Stage one download failed');
  download(await response.blob(), `${kpi}.csv`, CSV_MIME_TYPE);
};

export const downloadKpiDebug = async (kpi: string): Promise<void> => {
  const { Bucket, Key } = await awaitJSON<{ Bucket: string; Key: string }>(
    await StreamingUtilsLambdaFetch('/kpi_debug_zip', { params: { kpi } })
  );
  await pollS3({
    bucket: Bucket,
    mimeType: 'application/zip',
    filename: Key,
    overloadFilename: `${kpi}.zip`
  });
};
